import React, { useEffect, useState } from 'react';
import { api } from '../api';


const FILE_STRUCTURE_FIELDS = [
  { key: 'fio', label: 'ФИО' },
  { key: 'last_name', label: 'Фамилия' },
  { key: 'first_name', label: 'Имя' },
  { key: 'middle_name', label: 'Отчество' },
  { key: 'phone', label: 'Телефон' },
  { key: 'company', label: 'Компания' },
  { key: 'email', label: 'Email' },
  { key: 'inn', label: 'ИНН' },
  { key: 'ogrn', label: 'ОГРН' },
  { key: 'city', label: 'Город' }
];

const DEFAULT_SETTINGS = {
  skorozvon_api: {
    login: '',
    apiKey: '',
    clientId: '',
    clientSecret: ''
  },
  bank_api: {
    apiKey: '',
    apiUrl: 'https://partner.alfabank.ru/public-api/v2/leads',
    checkUrl: 'https://partner.alfabank.ru/public-api/v2/checks',
    rateLimitPerSecond: 4,
    liveMode: false,
    precheckBeforeApplication: false,
    logApplications: false
  },
  file_structure: {
    fio: { enabled: true, column: 1 },
    last_name: { enabled: false, column: 2 },
    first_name: { enabled: false, column: 3 },
    middle_name: { enabled: false, column: 4 },
    phone: { enabled: true, column: 5 },
    company: { enabled: true, column: 6 },
    email: { enabled: true, column: 7 },
    inn: { enabled: true, column: 8 },
    ogrn: { enabled: true, column: 9 },
    city: { enabled: true, column: 10 }
  },

  positive_call_statuses: ['Интерес', 'Готов к заявке'],
  negative_call_statuses: [],
  call_status_product_mappings: [],
  bank_check_mode: {
    combined: true
  },
  integration_payload_logging: {
    enabled: false,
    collectSuccessful: false,
    collectFailed: true,
    collectBankChecks: false,
    collectWebhookSuccess: false
  }
};

function mergeSettings(data = {}) {
  return {
    ...DEFAULT_SETTINGS,
    ...data,
    skorozvon_api: { ...DEFAULT_SETTINGS.skorozvon_api, ...(data.skorozvon_api || {}) },
    bank_api: { ...DEFAULT_SETTINGS.bank_api, ...(data.bank_api || {}) },
    bank_check_mode: { ...DEFAULT_SETTINGS.bank_check_mode, ...(data.bank_check_mode || {}) },
    file_structure: Object.keys(DEFAULT_SETTINGS.file_structure).reduce((acc, key) => {
      acc[key] = { ...DEFAULT_SETTINGS.file_structure[key], ...((data.file_structure || {})[key] || {}) };
      return acc;
    }, {}),
    integration_payload_logging: { ...DEFAULT_SETTINGS.integration_payload_logging, ...(data.integration_payload_logging || {}) },
    positive_call_statuses: Array.isArray(data.positive_call_statuses) ? data.positive_call_statuses : DEFAULT_SETTINGS.positive_call_statuses,
    negative_call_statuses: Array.isArray(data.negative_call_statuses) ? data.negative_call_statuses : DEFAULT_SETTINGS.negative_call_statuses,
    call_status_product_mappings: Array.isArray(data.call_status_product_mappings) ? data.call_status_product_mappings : DEFAULT_SETTINGS.call_status_product_mappings
  };
}

const SETTINGS_TABS = [
  { key: 'skorozvon', label: 'Скорозвон' },
  { key: 'bank', label: 'Банк' },
  { key: 'file_structure', label: 'Структура файла' },
  { key: 'call_statuses', label: 'Статусы звонков' },
  { key: 'cities', label: 'Города' },
  { key: 'logging', label: 'Логирование интеграций' }
];

export function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('skorozvon');
  const [cities, setCities] = useState([]);
  const [citySaving, setCitySaving] = useState(false);
  const [cityForm, setCityForm] = useState({
    id: null,
    inn_prefix: '',
    city_name: '',
    city_code: ''
  });

  function updateStringList(settingKey, index, value) {
    setSettings({
      ...settings,
      [settingKey]: (settings[settingKey] || []).map((item, itemIndex) =>
        itemIndex === index ? value : item
      )
    });
  }

  function addStringListItem(settingKey) {
    setSettings({
      ...settings,
      [settingKey]: [...(settings[settingKey] || []), '']
    });
  }

  function removeStringListItem(settingKey, index) {
    setSettings({
      ...settings,
      [settingKey]: (settings[settingKey] || []).filter((_, itemIndex) => itemIndex !== index)
    });
  }

  function normalizeStringList(settingKey) {
    setSettings({
      ...settings,
      [settingKey]: (settings[settingKey] || []).map((item) => String(item || '').trim()).filter(Boolean)
    });
  }

        
  function updateCallStatusMapping(index, patch) {
    setSettings({
      ...settings,
      call_status_product_mappings: settings.call_status_product_mappings.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item
      )
    });
  }

  function addCallStatusMapping() {
    setSettings({
      ...settings,
      call_status_product_mappings: [
        ...(settings.call_status_product_mappings || []),
        { statusName: '', productCode: '', advCode: '' }
      ]
    });
  }

  function removeCallStatusMapping(index) {
    setSettings({
      ...settings,
      call_status_product_mappings: settings.call_status_product_mappings.filter((_, itemIndex) => itemIndex !== index)
    });
  }

  async function loadCities() {
    const data = await api('/settings/cities');
    setCities(Array.isArray(data) ? data : []);
  }

  function resetCityForm() {
    setCityForm({
      id: null,
      inn_prefix: '',
      city_name: '',
      city_code: ''
    });
  }

  function editCity(city) {
    setCityForm({
      id: city.id,
      inn_prefix: city.inn_prefix || '',
      city_name: city.city_name || '',
      city_code: city.city_code || ''
    });
  }

  async function saveCity() {
    setCitySaving(true);
    setError('');
    setSaveMessage('');

    try {
      const payload = {
        inn_prefix: cityForm.inn_prefix,
        city_name: cityForm.city_name,
        city_code: cityForm.city_code
      };

      const data = cityForm.id
        ? await api(`/settings/cities/${cityForm.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : await api('/settings/cities', { method: 'POST', body: JSON.stringify(payload) });

      setCities(Array.isArray(data) ? data : []);
      resetCityForm();
      setSaveMessage('Город сохранен');
    } catch (err) {
      setError(err.message || 'Не удалось сохранить город');
    } finally {
      setCitySaving(false);
    }
  }

  async function deleteCity(id) {
    if (!window.confirm('Удалить город?')) return;

    try {
      const data = await api(`/settings/cities/${id}`, { method: 'DELETE' });
      setCities(Array.isArray(data) ? data : []);
      setSaveMessage('Город удален');
    } catch (err) {
      setError(err.message || 'Не удалось удалить город');
    }
  }
  
  function renderTabContent() {
    if (!settings) return null;

    switch (activeTab) {
      case 'skorozvon':
        return (
          <section className="card">
            <h2>Скорозвон</h2>
            <label>Login<input value={settings.skorozvon_api.login} onChange={(e) => setSettings({ ...settings, skorozvon_api: { ...settings.skorozvon_api, login: e.target.value } })} /></label>
            <label>API key<input value={settings.skorozvon_api.apiKey} onChange={(e) => setSettings({ ...settings, skorozvon_api: { ...settings.skorozvon_api, apiKey: e.target.value } })} /></label>
            <label>Client ID<input value={settings.skorozvon_api.clientId} onChange={(e) => setSettings({ ...settings, skorozvon_api: { ...settings.skorozvon_api, clientId: e.target.value } })} /></label>
            <label>Client secret<input value={settings.skorozvon_api.clientSecret} onChange={(e) => setSettings({ ...settings, skorozvon_api: { ...settings.skorozvon_api, clientSecret: e.target.value } })} /></label>
          </section>
        );
      case 'bank':
        return (
            <section className="card">
                <h2>Банк</h2>
                <label>API key<input value={settings.bank_api.apiKey} onChange={(e) => setSettings({
                    ...settings,
                    bank_api: {...settings.bank_api, apiKey: e.target.value}
                })}/></label>
                <label>API url<input value={settings.bank_api.apiUrl || ''} onChange={(e) => setSettings({
                    ...settings,
                    bank_api: {...settings.bank_api, apiUrl: e.target.value}
                })}/></label>
                <label>Check API url<input value={settings.bank_api.checkUrl || ''} onChange={(e) => setSettings({
                    ...settings,
                    bank_api: {...settings.bank_api, checkUrl: e.target.value}
                })}/></label>
                <label>Лимит запросов в секунду
                    <input
                        type="number"
                        min="1"
                        max="5"
                        value={settings.bank_api.rateLimitPerSecond || 4}
                        onChange={(e) => setSettings({
                            ...settings,
                            bank_api: {
                                ...settings.bank_api,
                                rateLimitPerSecond: Math.max(1, Math.min(5, Number(e.target.value) || 1))
                            }
                        })}
                    />
                </label>
                <label className="checkbox"><input type="checkbox" checked={Boolean(settings.bank_api.liveMode)}
                                                   onChange={(e) => setSettings({
                                                       ...settings,
                                                       bank_api: {...settings.bank_api, liveMode: e.target.checked}
                                                   })}/>Боевой режим</label>
                <label className="checkbox"><input type="checkbox"
                                                   checked={Boolean(settings.bank_api.precheckBeforeApplication)}
                                                   onChange={(e) => setSettings({
                                                       ...settings,
                                                       bank_api: {
                                                           ...settings.bank_api,
                                                           precheckBeforeApplication: e.target.checked
                                                       }
                                                   })}/>Проверять контакт банком перед заявкой</label>
                <label className="checkbox"><input type="checkbox"
                                                   checked={Boolean(settings.bank_api.logApplications)}
                                                   onChange={(e) => setSettings({
                                                       ...settings,
                                                       bank_api: {...settings.bank_api, logApplications: e.target.checked}
                                                   })}/>Логировать отправку заявок в банк</label>
                <label className="checkbox"><input type="checkbox" checked={settings.bank_check_mode.combined}
                                                   onChange={(e) => setSettings({
                                                       ...settings,
                                                       bank_check_mode: {combined: e.target.checked}
                                                   })}/>Одновременная проверка банком</label>
            </section>
        );
        case 'file_structure':
            return (
                <section className="card">
                    <h2>Структура файла</h2>
                    <div className="settings-grid">
                        {FILE_STRUCTURE_FIELDS.map((field) => (
                            <div key={field.key} className="mapping-row">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={settings.file_structure[field.key]?.enabled !== false}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          file_structure: {
                            ...settings.file_structure,
                            [field.key]: {
                              ...settings.file_structure[field.key],
                              enabled: e.target.checked
                            }
                          }
                        })
                      }
                    />
                    {field.label}
                  </label>
                  <label>Колонка
                    <input
                      type="number"
                      min="1"
                      value={settings.file_structure[field.key]?.column || ''}
                      disabled={settings.file_structure[field.key]?.enabled === false}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          file_structure: {
                            ...settings.file_structure,
                            [field.key]: {
                              ...settings.file_structure[field.key],
                              column: Number(e.target.value) || 1
                            }
                          }
                        })
                      }
                    />
                  </label>
                </div>
              ))}
            </div>
          </section>
        );
      case 'call_statuses':
        return (
          <section className="card">
            <h2>Статусы звонков</h2>
            <div className="section-subtitle">Положительные статусы</div>
            <div className="settings-grid top-gap">
              {(settings.positive_call_statuses || []).map((item, index) => (
                <div key={`positive-status-${index}`} className="mapping-row status-list-row">
                  <label>
                    Статус
                    <input
                      value={item || ''}
                      onChange={(e) => updateStringList('positive_call_statuses', index, e.target.value)}
                      onBlur={() => normalizeStringList('positive_call_statuses')}
                    />
                  </label>
                  <button type="button" className="danger" onClick={() => removeStringListItem('positive_call_statuses', index)}>Удалить</button>
                </div>
              ))}
            </div>
            <div className="inline-actions top-gap">
              <button type="button" onClick={() => addStringListItem('positive_call_statuses')}>Добавить статус</button>
            </div>

            <div className="top-gap">
              <div className="section-subtitle">Негативные статусы</div>
              <div className="settings-grid top-gap">
                {(settings.negative_call_statuses || []).map((item, index) => (
                  <div key={`negative-status-${index}`} className="mapping-row status-list-row">
                    <label>
                      Статус
                      <input
                        value={item || ''}
                        onChange={(e) => updateStringList('negative_call_statuses', index, e.target.value)}
                        onBlur={() => normalizeStringList('negative_call_statuses')}
                      />
                    </label>
                    <button type="button" className="danger" onClick={() => removeStringListItem('negative_call_statuses', index)}>Удалить</button>
                  </div>
                ))}
              </div>
              <div className="inline-actions top-gap">
                <button type="button" onClick={() => addStringListItem('negative_call_statuses')}>Добавить статус</button>
              </div>
            </div>

            <div className="top-gap">
              <div className="section-subtitle">Связь статусов и коды продуктов</div>
              <div className="settings-grid top-gap">
                {(settings.call_status_product_mappings || []).map((item, index) => (
                    <div key={`mapping-${index}`} className="mapping-row">
                      <label>
                        Статус звонка <br/>
                        <input
                            className="status_chain-input"
                            value={item.statusName || ''}
                            onChange={(e) => updateCallStatusMapping(index, {statusName: e.target.value})}
                        />
                      </label>
                      <br/>
                      <label>
                        Product code(s) <br/>
                        <input
                            className="productCode_chain-input"
                            value={item.productCode || ''}
                            onChange={(e) => updateCallStatusMapping(index, {productCode: e.target.value})}
                        />
                      </label>
                      <br/>
                      <label>
                        Adv Code <br/>
                        <input
                            className="advCode_chain-input"
                            value={item.advCode || ''}
                            onChange={(e) => updateCallStatusMapping(index, {advCode: e.target.value})}
                        />
                      </label>
                      <button type="button" className="danger" onClick={() => removeCallStatusMapping(index)}>Удалить
                      </button>
                    </div>
                ))}
              </div>
              <div className="inline-actions top-gap">
                <button type="button" onClick={addCallStatusMapping}>Добавить связь</button>
              </div>
            </div>
          </section>
        );
      case 'cities':
        return (
          <section className="card">
            <h2>Города</h2>
            <p className="muted">
              Эти города используются на странице «Лиды» для определения cityCode.
              Формат основной пары: «Город» — «Идентификатор». Код ИНН опционален и нужен для автоопределения города по первым двум цифрам ИНН.
            </p>

            <div className="grid two-columns compact-grid">
              <div className="card">
                <h3>{cityForm.id ? 'Редактировать город' : 'Добавить город'}</h3>

                <label>
                  Код ИНН, опционально
                  <input
                    value={cityForm.inn_prefix}
                    onChange={(e) => setCityForm({ ...cityForm, inn_prefix: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                    placeholder="54"
                  />
                </label>

                <label>
                  Город
                  <input
                    value={cityForm.city_name}
                    onChange={(e) => setCityForm({ ...cityForm, city_name: e.target.value })}
                    placeholder="Новосибирск"
                  />
                </label>

                <label>
                  Идентификатор
                  <input
                    value={cityForm.city_code}
                    onChange={(e) => setCityForm({ ...cityForm, city_code: e.target.value })}
                    placeholder="8dea00e3-9aab-4d8e-887c-ef2aaa546456"
                  />
                </label>

                <div className="inline-actions">
                  <button type="button" onClick={saveCity} disabled={citySaving}>
                    {citySaving ? 'Сохраняем...' : cityForm.id ? 'Сохранить город' : 'Добавить город'}
                  </button>
                  {cityForm.id ? <button type="button" onClick={resetCityForm}>Отмена</button> : null}
                </div>
              </div>

              <div className="card">
                <h3>Список городов</h3>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Код ИНН</th>
                        <th>Город</th>
                        <th>Идентификатор</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {cities.map((city) => (
                        <tr key={city.id}>
                          <td>{city.inn_prefix || '-'}</td>
                          <td>{city.city_name}</td>
                          <td>{city.city_code}</td>
                          <td>
                            <button type="button" onClick={() => editCity(city)}>Изменить</button>{' '}
                            <button type="button" className="danger" onClick={() => deleteCity(city.id)}>Удалить</button>
                          </td>
                        </tr>
                      ))}
                      {!cities.length ? <tr><td colSpan="4">Города еще не добавлены</td></tr> : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        );
      case 'logging':
        return (
            <section className="card">
              <h2>Логирование интеграций</h2>
              <label className="checkbox"><input type="checkbox" checked={settings.integration_payload_logging.enabled}
                                                 onChange={(e) => setSettings({
                                                   ...settings,
                                                   integration_payload_logging: {
                                                     ...settings.integration_payload_logging,
                                                     enabled: e.target.checked
                                                   }
                                                 })}/>Собирать payload логи</label>
              <label className="checkbox"><input type="checkbox"
                                                 checked={settings.integration_payload_logging.collectSuccessful}
                                                 onChange={(e) => setSettings({
                                                   ...settings,
                                                   integration_payload_logging: {
                                                     ...settings.integration_payload_logging,
                                                     collectSuccessful: e.target.checked
                                                   }
                                                 })}/>Собирать успешные</label>
              <label className="checkbox"><input type="checkbox"
                                                 checked={Boolean(settings.integration_payload_logging.collectWebhookSuccess)}
                                                 onChange={(e) => setSettings({
                                                   ...settings,
                                                   integration_payload_logging: {
                                                     ...settings.integration_payload_logging,
                                                     collectWebhookSuccess: e.target.checked
                                                   }
                                                 })}/>Получать успешные данные по вебхуку</label>
              <label className="checkbox"><input type="checkbox"
                                                 checked={settings.integration_payload_logging.collectFailed}
                                                 onChange={(e) => setSettings({
                                                   ...settings,
                                                   integration_payload_logging: {
                                                     ...settings.integration_payload_logging,
                                                     collectFailed: e.target.checked
                                                   }
                                                 })}/>Собирать неудачные</label>
              <label className="checkbox"><input type="checkbox"
                                                 checked={Boolean(settings.integration_payload_logging.collectBankChecks)}
                                                 onChange={(e) => setSettings({
                                                   ...settings,
                                                   integration_payload_logging: {
                                                     ...settings.integration_payload_logging,
                                                     collectBankChecks: e.target.checked
                                                   }
                                                 })}/>Собирать проверку банком</label>
            </section>
        );
      default:
        return null;
    }
  }

  useEffect(() => {
    api('/settings')
        .then((data) => {
          setSettings(mergeSettings(data));
          setError('');
          setSaveMessage('');
        })
        .catch((error) => setError(error.message || 'Не удалось загрузить настройки'));
  }, []);

  useEffect(() => {
    if (activeTab !== 'cities') return;

    loadCities().catch((err) => {
      setError(err.message || 'Не удалось загрузить города');
    });
  }, [activeTab]);

  if (!settings) return <div className="card">{error || 'Загрузка настроек...'}</div>;

  async function save() {
    setSaving(true);
    setSaveMessage('');
    setError('');
    try {
      await api('/settings', { method: 'PUT', body: JSON.stringify(settings) });
      const freshSettings = await api('/settings');
      setSettings(mergeSettings(freshSettings));
      setSaveMessage('Сохранено и подтверждено повторным чтением');
    } catch (error) {
      setError(error.message || 'Не удалось сохранить настройки');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page-stack settings-page">
      {saveMessage ? <section className="card success-banner">{saveMessage}</section> : null}
      {error ? <section className="card error-banner">{error}</section> : null}

      <section className="card">
        <div className="settings-tabs" role="tablist" aria-label="Разделы настроек">
          {SETTINGS_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`tab-button${activeTab === tab.key ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {renderTabContent()}

      <section className="card">
        <div className="inline-actions">
          <button type="button" onClick={save} disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить настройки'}</button>
        </div>
      </section>
    </div>
  );
}
